export type SidebarSectionListMetrics = {
  heightSnapPoints: number[];
  paddingBottom: number;
  paddingTop: number;
  scrollSnapPoints: number[];
};

type SidebarSectionHeightSnapOptions = {
  clamp: (height: number) => number;
  fallbackMinHeight: number;
  minimumVisibleItems?: number;
  targetSelector?: string;
};

type SidebarSectionMeasureOptions = {
  targetSelector?: string;
};

const numberFromCssPx = (value: string) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const hasVisibleBox = (element: HTMLElement) => {
  if (element.closest('[inert], [aria-hidden="true"]')) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return (
    element.getClientRects().length > 0 && rect.width > 0 && rect.height > 0
  );
};

const firstVisibleBox = (element: Element): HTMLElement | null => {
  if (element instanceof HTMLElement && hasVisibleBox(element)) {
    return element;
  }

  return (
    Array.from(element.querySelectorAll<HTMLElement>('*')).find(
      hasVisibleBox
    ) ?? null
  );
};

export const nearestSidebarSectionSnapPoint = (
  value: number,
  snapPoints: number[]
) =>
  snapPoints.reduce(
    (nearest, point) =>
      Math.abs(point - value) < Math.abs(nearest - value) ? point : nearest,
    snapPoints[0]
  );

export const minimumSidebarSectionSnapPoint = (
  snapPoints: number[],
  minimumVisibleItems = 1
) => {
  if (snapPoints.length === 0) return null;

  const minimumIndex = Math.min(
    snapPoints.length - 1,
    Math.max(1, Math.floor(minimumVisibleItems)) - 1
  );
  return snapPoints[minimumIndex];
};

const getSidebarListScrollTop = (element: HTMLElement) => {
  const viewport = element.closest<HTMLElement>(
    '[data-sidebar-scroll-viewport="true"]'
  );
  return viewport instanceof HTMLElement &&
    viewport.dataset.sidebarScrollViewport === 'true'
    ? viewport.scrollTop
    : 0;
};

export const measureSidebarSectionListMetrics = (
  element: HTMLElement | null,
  options: SidebarSectionMeasureOptions = {}
): SidebarSectionListMetrics | null => {
  if (!element) return null;

  const candidateElements = options.targetSelector
    ? Array.from(element.querySelectorAll<HTMLElement>(options.targetSelector))
    : Array.from(element.children);
  const targets = (
    candidateElements.length > 0
      ? candidateElements
      : Array.from(element.children)
  )
    .map(firstVisibleBox)
    .filter((target): target is HTMLElement => target !== null);
  if (targets.length === 0) return null;

  const style = window.getComputedStyle(element);
  const listRect = element.getBoundingClientRect();
  const scrollTop = getSidebarListScrollTop(element);
  const paddingTop = numberFromCssPx(style.paddingTop);
  const paddingBottom = numberFromCssPx(style.paddingBottom);
  const targetRects = targets.map(target => target.getBoundingClientRect());
  const heightSnapPoints = targetRects
    .map((rect, index) => {
      const nextRect = targetRects[index + 1];
      const trailingSpace = nextRect
        ? Math.max(0, nextRect.top - rect.bottom) / 2
        : paddingBottom;
      return rect.bottom - listRect.top - scrollTop + trailingSpace;
    })
    .filter(point => point > 0);

  return {
    heightSnapPoints,
    paddingBottom,
    paddingTop,
    scrollSnapPoints: [
      0,
      ...targetRects.map(rect =>
        Math.max(0, rect.top - listRect.top - paddingTop)
      ),
    ],
  };
};

export const snapSidebarSectionListHeight = (
  height: number,
  element: HTMLElement | null,
  options: SidebarSectionHeightSnapOptions
) => {
  const metrics = measureSidebarSectionListMetrics(element, {
    targetSelector: options.targetSelector,
  });
  if (!metrics) {
    return options.clamp(Math.max(options.fallbackMinHeight, height));
  }

  if (metrics.heightSnapPoints.length === 0) {
    return options.clamp(height);
  }

  const minimumHeight =
    minimumSidebarSectionSnapPoint(
      metrics.heightSnapPoints,
      options.minimumVisibleItems
    ) ?? options.fallbackMinHeight;
  const eligibleSnapPoints = metrics.heightSnapPoints.filter(
    point => point >= minimumHeight
  );

  if (eligibleSnapPoints.length === 0) {
    return options.clamp(minimumHeight);
  }

  const snappedHeight = nearestSidebarSectionSnapPoint(
    Math.max(height, minimumHeight),
    eligibleSnapPoints
  );
  const fullListHeight =
    metrics.heightSnapPoints[metrics.heightSnapPoints.length - 1];

  return options.clamp(Math.min(snappedHeight, fullListHeight));
};

export const clampSidebarSectionListHeight = (
  height: number,
  element: HTMLElement | null,
  options: SidebarSectionHeightSnapOptions
) => {
  const metrics = measureSidebarSectionListMetrics(element, {
    targetSelector: options.targetSelector,
  });
  const measuredMinimumHeight = metrics
    ? minimumSidebarSectionSnapPoint(
        metrics.heightSnapPoints,
        options.minimumVisibleItems
      )
    : null;
  const minimumHeight = measuredMinimumHeight ?? options.fallbackMinHeight;

  return options.clamp(Math.max(minimumHeight, height));
};

export const snapSidebarSectionListScrollTop = (
  scrollTop: number,
  element: HTMLElement | null,
  options: SidebarSectionMeasureOptions = {}
) => {
  if (scrollTop <= 1) return 0;

  const metrics = measureSidebarSectionListMetrics(element, options);
  if (!metrics || metrics.scrollSnapPoints.length === 0) return scrollTop;

  return nearestSidebarSectionSnapPoint(scrollTop, metrics.scrollSnapPoints);
};
